import React, { useState, useEffect, useCallback } from 'react';
import { MdEdit, MdSave, MdClose, MdAdd, MdDelete } from 'react-icons/md';
import { hfService } from '../../services/hfService';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  aum_usd: '', mgmt_fee_bps: '', perf_fee_bps: '', high_water_mark: false,
  lockup_months: '', liquidity: '', redemption_notice_days: '',
  pm_name: '', pm_email: '', ir_contact_name: '', ir_email: '', ir_phone: '',
  strategy_description: '', custom_fields: {},
};

const labelCls = 'text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block';
const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none';
const readCls = 'text-sm text-gray-800 dark:text-gray-200 py-1.5';
const na = <span className="text-gray-400">—</span>;

const fmtMoney = (v) => v == null ? na : `$${Number(v).toLocaleString()}`;
const fmtBps = (v) => v == null ? na : `${(v / 100).toFixed(2)}%`;
const fmtNum = (v, suffix = '') => v == null || v === '' ? na : `${v}${suffix}`;

const FundProfileSection = ({ fundId }) => {
  const { user } = useAuth();
  const canEdit = !!user?.is_admin || !!user?.profile?.is_hf_admin;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState('');

  const load = useCallback(async () => {
    if (!fundId) return;
    setLoading(true);
    try {
      const res = await hfService.getFundProfile(fundId);
      setProfile(res.data);
      setForm({
        ...emptyForm,
        ...res.data,
        custom_fields: res.data.custom_fields || {},
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [fundId]);

  useEffect(() => { load(); }, [load]);

  const onChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Convert numeric strings to numbers / null
      const numericKeys = ['aum_usd', 'mgmt_fee_bps', 'perf_fee_bps', 'lockup_months', 'redemption_notice_days'];
      const payload = { ...form };
      numericKeys.forEach(k => {
        payload[k] = payload[k] === '' || payload[k] == null ? null : Number(payload[k]);
      });
      const res = await hfService.updateFundProfile(fundId, payload);
      setProfile(res.data);
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (profile) setForm({ ...emptyForm, ...profile, custom_fields: profile.custom_fields || {} });
    setEditing(false);
    setError(null);
  };

  const addCustomField = () => {
    if (!newKey.trim()) return;
    setForm(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, [newKey.trim()]: newVal } }));
    setNewKey(''); setNewVal('');
  };

  const removeCustomField = (k) => {
    setForm(prev => {
      const cf = { ...prev.custom_fields };
      delete cf[k];
      return { ...prev, custom_fields: cf };
    });
  };

  if (!fundId) return null;

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">Fund Profile</h3>
        {!editing ? (
          canEdit && (
            <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-brand-gold border border-gray-200 dark:border-gray-700 rounded-md">
              <MdEdit size={14} /> Edit
            </button>
          )
        ) : (
          <div className="flex gap-2">
            <button onClick={cancel} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md">
              <MdClose size={14} /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-brand-gold rounded-md hover:opacity-90 disabled:opacity-50">
              <MdSave size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="mb-3 text-xs text-red-500">{error}</div>}
      {loading && !profile && <div className="text-sm text-gray-500">Loading profile…</div>}

      {/* Terms */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Terms</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="AUM (USD)" editing={editing} value={form.aum_usd} onChange={(v) => onChange('aum_usd', v)} display={fmtMoney(profile?.aum_usd)} type="number" />
          <Field label="Mgmt Fee (bps)" editing={editing} value={form.mgmt_fee_bps} onChange={(v) => onChange('mgmt_fee_bps', v)} display={fmtBps(profile?.mgmt_fee_bps)} type="number" />
          <Field label="Perf Fee (bps)" editing={editing} value={form.perf_fee_bps} onChange={(v) => onChange('perf_fee_bps', v)} display={fmtBps(profile?.perf_fee_bps)} type="number" />
          <div>
            <label className={labelCls}>High Water Mark</label>
            {editing ? (
              <label className="inline-flex items-center gap-2 py-1.5">
                <input type="checkbox" checked={form.high_water_mark} onChange={(e) => onChange('high_water_mark', e.target.checked)} className="accent-brand-gold" />
                <span className="text-sm text-gray-800 dark:text-gray-200">{form.high_water_mark ? 'Yes' : 'No'}</span>
              </label>
            ) : (
              <div className={readCls}>{profile?.high_water_mark ? 'Yes' : 'No'}</div>
            )}
          </div>
          <Field label="Lockup (months)" editing={editing} value={form.lockup_months} onChange={(v) => onChange('lockup_months', v)} display={fmtNum(profile?.lockup_months, 'm')} type="number" />
          <Field label="Liquidity" editing={editing} value={form.liquidity} onChange={(v) => onChange('liquidity', v)} display={profile?.liquidity || na} placeholder="Monthly" />
          <Field label="Redemption Notice" editing={editing} value={form.redemption_notice_days} onChange={(v) => onChange('redemption_notice_days', v)} display={fmtNum(profile?.redemption_notice_days, ' days')} type="number" />
        </div>
      </div>

      {/* People */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">People</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="PM Name" editing={editing} value={form.pm_name} onChange={(v) => onChange('pm_name', v)} display={profile?.pm_name || na} />
          <Field label="PM Email" editing={editing} value={form.pm_email} onChange={(v) => onChange('pm_email', v)} display={profile?.pm_email || na} type="email" />
          <Field label="IR Contact" editing={editing} value={form.ir_contact_name} onChange={(v) => onChange('ir_contact_name', v)} display={profile?.ir_contact_name || na} />
          <Field label="IR Email" editing={editing} value={form.ir_email} onChange={(v) => onChange('ir_email', v)} display={profile?.ir_email || na} type="email" />
          <Field label="IR Phone" editing={editing} value={form.ir_phone} onChange={(v) => onChange('ir_phone', v)} display={profile?.ir_phone || na} />
        </div>
      </div>

      {/* Strategy */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Strategy Description</h4>
        {editing ? (
          <textarea
            rows={4} value={form.strategy_description || ''}
            onChange={(e) => onChange('strategy_description', e.target.value)}
            placeholder="Investment approach, target markets, leverage, hedging, etc."
            className={`${inputCls} resize-y`}
          />
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
            {profile?.strategy_description || <span className="text-gray-400">No description yet.</span>}
          </p>
        )}
      </div>

      {/* Custom fields */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Custom Fields</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(editing ? form.custom_fields : (profile?.custom_fields || {})).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/30 rounded-md px-3 py-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 min-w-0 truncate">{k}:</span>
              <span className="text-sm text-gray-800 dark:text-gray-200 flex-1 truncate">{v || na}</span>
              {editing && (
                <button onClick={() => removeCustomField(k)} className="text-red-500 hover:text-red-700" aria-label="Remove field">
                  <MdDelete size={16} />
                </button>
              )}
            </div>
          ))}
          {!editing && Object.keys(profile?.custom_fields || {}).length === 0 && (
            <div className="text-sm text-gray-400">No custom fields.</div>
          )}
        </div>
        {editing && (
          <div className="flex gap-2 mt-3">
            <input type="text" placeholder="Field name" value={newKey} onChange={(e) => setNewKey(e.target.value)} className={`${inputCls} flex-1`} />
            <input type="text" placeholder="Value" value={newVal} onChange={(e) => setNewVal(e.target.value)} className={`${inputCls} flex-1`} />
            <button onClick={addCustomField} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700">
              <MdAdd size={14} /> Add
            </button>
          </div>
        )}
      </div>

      {profile?.updated_by_name && (
        <div className="mt-4 text-[11px] text-gray-400">
          Last updated by {profile.updated_by_name} · {new Date(profile.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, editing, value, onChange, display, type = 'text', placeholder }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {editing ? (
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
      />
    ) : (
      <div className={readCls}>{display}</div>
    )}
  </div>
);

export default FundProfileSection;
